import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';

import { WorkflowClient } from './api/workflow-client';
import { WorkflowServiceEventListener } from './events/workflow-service-event-listener';
import { seedWorkflows } from './redux/workflow-slice';
import { Workflow } from './workflow';

declare global {
    interface WindowEventMap {
        'useWorkflow:request': CustomEvent<string>;
    }
}

const REQUEST_WORKFLOW_EVENT = 'useWorkflow:request';
const STORAGE_KEY = 'WFS_REQ';

export function workflowRequest(workflowId: string) {
    window.dispatchEvent(new CustomEvent(REQUEST_WORKFLOW_EVENT, { detail: workflowId }));
}

interface Props {
    dispatch: ThunkDispatch<any, any, AnyAction>;
    enablePubSub?: boolean;
}

export function useWorkflows(props: Props) {
    const [pending, changePending] = useState<string[]>([]);
    const [timeoutSet, changeTimeoutSet] = useState<boolean>(false);
    const [timeoutFinished, changeTimeoutFinished] = useState<boolean>(false);
    const [workflowEventListener, changeWorkflowEventListener] = useState<WorkflowServiceEventListener | null>(null);

    useEffect(() => {
        window.addEventListener(REQUEST_WORKFLOW_EVENT, listener);
    }, []);

    useEffect(() => {
        if (props.enablePubSub) {
            changeWorkflowEventListener(WorkflowServiceEventListener.getInstance(props.dispatch));
        }
    }, [props.enablePubSub]);

    useEffect(() => {
        if (timeoutFinished) {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored != null) {
                changePending(JSON.parse(stored));
                changeTimeoutSet(false);
                changeTimeoutFinished(false);
                localStorage.removeItem(STORAGE_KEY);
            }
        }
    }, [timeoutFinished]);

    useEffect(() => {
        if (pending.length > 0) {
            getWorkflows(pending).then((result) => {
                props.dispatch(seedWorkflows(result));
                changePending([]);
                changeTimeoutSet(false);
                changeTimeoutFinished(false);
            });
        }
    }, [pending]);

    const listener = (event: CustomEvent) => {
        if (typeof event.detail == 'string') {
            if (timeoutSet === false) {
                changeTimeoutSet(true);
                setTimeout(() => {
                    changeTimeoutFinished(true);
                }, 1000);
            }
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored == null) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify([event.detail]));
            } else if (!JSON.parse(stored).includes(event.detail)) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify([...JSON.parse(stored), event.detail]));
            }
        }
    };

    return { workflowEventListener };
}

async function getWorkflows(workflowIds: string[]): Promise<Workflow[]> {
    const response = await WorkflowClient.resolveWorkflows(workflowIds);
    return response.results;
}
